import React from "react";
import { useTranslation } from "react-i18next";
import {
  formatPastConditionDateLabel,
  parseTripDate,
} from "../pastTripAnalytics";

const SHOW_PAST_TRIPS_EXTRA_CHARTS = false;

function PastConditionsSection({ timeline, visibleMetrics }) {
  const { t } = useTranslation();
  const chartWidth = Math.max(640, timeline.length * 104);
  const chartHeight = 260;
  const padding = {
    top: 20,
    right: 24,
    bottom: 58,
    left: 24,
  };
  const plotWidth = Math.max(chartWidth - padding.left - padding.right, 1);
  const plotHeight = Math.max(chartHeight - padding.top - padding.bottom, 1);
  const labelStep = timeline.length > 7 ? Math.ceil(timeline.length / 7) : 1;

  return (
    <div className="mytrips-past-summary-section mytrips-past-summary-section--insight">
      <div className="mytrips-past-conditions-header">
        <div>
          <h4 className="mytrips-past-summary-section-title">
            {t("trip_conditions_over_trip", {
              defaultValue: "Conditions Over Trip",
            })}
          </h4>
          <p className="mytrips-past-conditions-summary">
            {t("trip_conditions_over_trip_summary", {
              defaultValue:
                "Daily historical conditions across the selected trip timeline. Only metrics with recorded data are shown.",
            })}
          </p>
        </div>
      </div>

      <div className="mytrips-past-conditions-chart-shell">
        <div className="mytrips-past-conditions-chart-scroll">
          <svg
            className="mytrips-past-conditions-chart-svg"
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            role="img"
            aria-label={t("trip_conditions_over_trip_aria", {
              defaultValue:
                "Line chart showing recorded trip conditions over time.",
            })}
          >
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
              const y = padding.top + plotHeight * ratio;

              return (
                <line
                  key={`grid-${ratio}`}
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + plotWidth}
                  y2={y}
                  className="mytrips-past-conditions-grid"
                />
              );
            })}

            <line
              x1={padding.left}
              y1={padding.top + plotHeight}
              x2={padding.left + plotWidth}
              y2={padding.top + plotHeight}
              className="mytrips-past-conditions-axis"
            />

            {visibleMetrics.map((metric) => {
              const points = metric.samples.map((sample) => {
                const x =
                  timeline.length === 1
                    ? padding.left + plotWidth / 2
                    : padding.left +
                      (sample.index / (timeline.length - 1)) * plotWidth;
                const y =
                  metric.min === metric.max
                    ? padding.top + plotHeight / 2
                    : padding.top +
                      (1 - (sample.value - metric.min) / (metric.max - metric.min)) *
                        plotHeight;

                return { ...sample, x, y };
              });

              const pathData = points
                .map((point, index) =>
                  `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
                )
                .join(" ");

              return (
                <g key={metric.key}>
                  <path
                    d={pathData}
                    className="mytrips-past-conditions-line"
                    style={{ stroke: metric.color }}
                  />

                  {points.map((point) => (
                    <circle
                      key={`${metric.key}-${point.date}`}
                      cx={point.x}
                      cy={point.y}
                      r="4"
                      className="mytrips-past-conditions-dot"
                      style={{ fill: metric.color }}
                    />
                  ))}
                </g>
              );
            })}

            {timeline.map((day, index) => {
              const x =
                timeline.length === 1
                  ? padding.left + plotWidth / 2
                  : padding.left + (index / (timeline.length - 1)) * plotWidth;
              const shouldRenderLabel =
                index % labelStep === 0 || index === timeline.length - 1;

              return (
                <g key={`axis-${day.date || index}`}>
                  <line
                    x1={x}
                    y1={padding.top + plotHeight}
                    x2={x}
                    y2={padding.top + plotHeight + 6}
                    className="mytrips-past-conditions-tick"
                  />
                  {shouldRenderLabel && (
                    <text
                      x={x}
                      y={padding.top + plotHeight + 24}
                      textAnchor="middle"
                      className="mytrips-past-conditions-label"
                    >
                      {formatPastConditionDateLabel(day.date)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      <p className="mytrips-past-conditions-note">
        {t("trip_conditions_over_trip_note", {
          defaultValue:
            "Each line is scaled to its own trip range for easier comparison.",
        })}
      </p>

      <div className="mytrips-past-conditions-legend">
        {visibleMetrics.map((metric) => (
          <div
            key={`legend-${metric.key}`}
            className="mytrips-past-conditions-legend-item"
          >
            <span
              className="mytrips-past-conditions-legend-swatch"
              style={{ "--trip-conditions-color": metric.color }}
            />
            <span className="mytrips-past-conditions-legend-copy">
              <span className="mytrips-past-conditions-legend-label">
                {metric.label}
              </span>
              <span className="mytrips-past-conditions-legend-value">
                {metric.rangeLabel}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PastCatchTimelineSection({
  timeline,
  buckets,
  tripTimelineStartMs,
  tripTimelineEndMs,
}) {
  const { t } = useTranslation();
  const chartWidth = Math.max(640, timeline.length * 104);
  const chartHeight = 260;
  const padding = {
    top: 20,
    right: 24,
    bottom: 58,
    left: 24,
  };
  const plotWidth = Math.max(chartWidth - padding.left - padding.right, 1);
  const plotHeight = Math.max(chartHeight - padding.top - padding.bottom, 1);
  const labelStep = timeline.length > 7 ? Math.ceil(timeline.length / 7) : 1;
  const maxCount = buckets.reduce(
    (max, bucket) => Math.max(max, bucket.cumulativeCount || 0),
    0
  );

  return (
    <div className="mytrips-past-summary-section mytrips-past-summary-section--insight">
      <div className="mytrips-past-conditions-header">
        <div>
          <h4 className="mytrips-past-summary-section-title">
            {t("trip_catch_activity_timeline", {
              defaultValue: "Catch Activity Timeline",
            })}
          </h4>
          <p className="mytrips-past-conditions-summary">
            {t("trip_catch_activity_timeline_summary", {
              defaultValue:
                "Shows when catches were recorded across the selected trip timeline as a running total.",
            })}
          </p>
        </div>
      </div>

      <div className="mytrips-past-conditions-chart-shell">
        <div className="mytrips-past-conditions-chart-scroll">
          <svg
            className="mytrips-past-conditions-chart-svg"
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            role="img"
            aria-label={t("trip_catch_activity_timeline_aria", {
              defaultValue:
                "Line chart showing catch activity over the selected trip timeline.",
            })}
          >
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
              const y = padding.top + plotHeight * ratio;

              return (
                <line
                  key={`grid-${ratio}`}
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + plotWidth}
                  y2={y}
                  className="mytrips-past-conditions-grid"
                />
              );
            })}

            <line
              x1={padding.left}
              y1={padding.top + plotHeight}
              x2={padding.left + plotWidth}
              y2={padding.top + plotHeight}
              className="mytrips-past-conditions-axis"
            />

            <path
              d={buckets
                .map((bucket, index) => {
                  const x =
                    padding.left +
                    ((bucket.timestamp - tripTimelineStartMs) /
                      (tripTimelineEndMs - tripTimelineStartMs)) *
                      plotWidth;
                  const y =
                    padding.top +
                    (1 - bucket.cumulativeCount / maxCount) * plotHeight;

                  return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
                })
                .join(" ")}
              className="mytrips-past-catch-line"
            />

            {buckets.map((bucket) => {
              const x =
                padding.left +
                ((bucket.timestamp - tripTimelineStartMs) /
                  (tripTimelineEndMs - tripTimelineStartMs)) *
                  plotWidth;
              const y =
                padding.top +
                (1 - bucket.cumulativeCount / maxCount) * plotHeight;

              return (
                <circle
                  key={`catch-point-${bucket.key}`}
                  cx={x}
                  cy={y}
                  r="4.5"
                  className="mytrips-past-catch-dot"
                >
                  <title>
                    {`${bucket.label}: ${bucket.count} catch${
                      bucket.count === 1 ? "" : "es"
                    } recorded (${bucket.cumulativeCount} total)`}
                  </title>
                </circle>
              );
            })}

            {timeline.map((day, index) => {
              if (!parseTripDate(day.date)) return null;

              const x =
                timeline.length === 1
                  ? padding.left + plotWidth / 2
                  : padding.left + (index / (timeline.length - 1)) * plotWidth;
              const shouldRenderLabel =
                index % labelStep === 0 || index === timeline.length - 1;

              return (
                <g key={`catch-axis-${day.date || index}`}>
                  <line
                    x1={x}
                    y1={padding.top + plotHeight}
                    x2={x}
                    y2={padding.top + plotHeight + 6}
                    className="mytrips-past-conditions-tick"
                  />
                  {shouldRenderLabel && (
                    <text
                      x={x}
                      y={padding.top + plotHeight + 24}
                      textAnchor="middle"
                      className="mytrips-past-conditions-label"
                    >
                      {formatPastConditionDateLabel(day.date)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      <p className="mytrips-past-conditions-note">
        {t("trip_catch_activity_timeline_note", {
          defaultValue:
            "Timed catches use their recorded time. Date-only catches are placed on their trip date without added time precision.",
        })}
      </p>
    </div>
  );
}

export default function PastTripSummaryInsights({
  pastSummary,
  weatherConditions,
}) {
  const { t } = useTranslation();
  const {
    summarySpeciesCounts,
    summaryByWeight,
    pastConditionTimeline,
    visiblePastConditionMetrics,
    hasPastCatchTimelineChart,
    pastCatchTimelineBuckets,
    tripTimelineStartMs,
    tripTimelineEndMs,
  } = pastSummary;

  return (
    <>
      <div className="mytrips-past-summary-section mytrips-past-summary-section--insight">
        <h4 className="mytrips-past-summary-section-title">
          {t("trip_species_summary", {
            defaultValue: "Species Summary",
          })}
        </h4>

        {summarySpeciesCounts.length > 0 ? (
          <div className="mytrips-past-summary-table-wrap">
            <table className="mytrips-past-summary-table">
              <thead>
                <tr>
                  <th>{t("fish", { defaultValue: "Fish" })}</th>
                  <th>{t("count", { defaultValue: "Count" })}</th>
                </tr>
              </thead>
              <tbody>
                {summarySpeciesCounts.map((row) => (
                  <tr key={row.species}>
                    <td>{row.species}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mytrips-past-summary-empty">
            {t("trip_species_summary_empty", {
              defaultValue: "No species data available.",
            })}
          </p>
        )}
      </div>

      <div className="mytrips-past-summary-section mytrips-past-summary-section--insight">
        <h4 className="mytrips-past-summary-section-title">
          {t("trip_weight_summary", {
            defaultValue: "Biggest Catches",
          })}
        </h4>

        {summaryByWeight.length > 0 ? (
          <div className="mytrips-past-summary-table-wrap">
            <table className="mytrips-past-summary-table">
              <thead>
                <tr>
                  <th>{t("fish", { defaultValue: "Fish" })}</th>
                  <th>{t("weight", { defaultValue: "Weight" })}</th>
                  <th>{t("date", { defaultValue: "Date" })}</th>
                </tr>
              </thead>
              <tbody>
                {summaryByWeight.map((item) => (
                  <tr key={`weight-${item.id}`}>
                    <td>{item.fish || "\u2014"}</td>
                    <td>{item.weight || "\u2014"}</td>
                    <td>
                      {item.savedCatchDate || "\u2014"}
                      {item.savedCatchTime ? ` ${item.savedCatchTime}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mytrips-past-summary-empty">
            {t("trip_weight_summary_empty", {
              defaultValue: "No weight data available.",
            })}
          </p>
        )}
      </div>

      {SHOW_PAST_TRIPS_EXTRA_CHARTS &&
        (!weatherConditions ? (
          <p className="mytrips-past-summary-empty">
            {t("trip_conditions_over_trip_loading", {
              defaultValue: "Loading historical condition trends...",
            })}
          </p>
        ) : pastConditionTimeline.length > 0 &&
          visiblePastConditionMetrics.length > 0 ? (
          <PastConditionsSection
            timeline={pastConditionTimeline}
            visibleMetrics={visiblePastConditionMetrics}
          />
        ) : (
          <p className="mytrips-past-summary-empty">
            {t("trip_conditions_over_trip_empty", {
              defaultValue:
                "There is not enough recorded historical condition data to chart this trip yet.",
            })}
          </p>
        ))}

      {SHOW_PAST_TRIPS_EXTRA_CHARTS &&
        (hasPastCatchTimelineChart ? (
          <PastCatchTimelineSection
            timeline={pastConditionTimeline}
            buckets={pastCatchTimelineBuckets}
            tripTimelineStartMs={tripTimelineStartMs}
            tripTimelineEndMs={tripTimelineEndMs}
          />
        ) : (
          <p className="mytrips-past-summary-empty">
            {t("trip_catch_activity_timeline_empty", {
              defaultValue:
                "There are not enough recorded catch timestamps to chart activity for this trip yet.",
            })}
          </p>
        ))}
    </>
  );
}
